import { dateSchema } from "@medoma/models/date-and-time";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { logisticsApi } from "./ApiClient";
import { knownFeatureFlagsSchema, useFeatureFlag } from "./FeatureFlags";

const activeVehicleSchema = z.object({
  id: z.uuid(),
  alias: z.string().nullish(),
  licensePlate: z.string().nullish(),
  location: z.object({
    latitude: z.number(),
    longitude: z.number(),
    inMovement: z.boolean(),
    timestamp: dateSchema,
  }),
  driver: z
    .object({
      id: z.uuid(),
      name: z.string(),
      phoneNumber: z.string(),
      email: z.string().nullish(),
    })
    .nullish(),
});

type IActiveVehicle = z.infer<typeof activeVehicleSchema>;

const vehicleSchema = activeVehicleSchema.extend({
  location: activeVehicleSchema.shape.location.nullish(),
});

async function fetchVehicles() {
  const parsedVehicles = await logisticsApi
    .get(`/vehicles`)
    .json(z.array(vehicleSchema));
  return parsedVehicles.filter((vehicle): vehicle is IActiveVehicle =>
    Boolean(vehicle.location),
  );
}

export const useVehicles = () => {
  const { data: shouldRequestVehicles } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.PERMANENT_UseAbax,
  );
  return useQuery({
    queryKey: ["vehicles", shouldRequestVehicles],
    queryFn: fetchVehicles,
    staleTime: 1000 * 60 * 10, // 10 minutes
    refetchInterval: 1000 * 30, // 30 seconds
    enabled: shouldRequestVehicles !== false,
  });
};
